import React from "react";

import {DocumentTextIcon, ScaleIcon} from "@heroicons/react/24/outline";
import NumberFlow from "@number-flow/react";
import {motion} from "framer-motion";

import * as graphics from "@client/app/graphics";
import * as router from "@client/router";

import {SimpleIcon} from "@client/components/Icon";
import {Streak} from "@client/components/Navigation";
import {useAppSelector} from "@client/store";
import {classNames} from "@reactivated";

type Props = Parameters<Parameters<typeof MyFitnessPalScreen.withIndex>[0]>[0];

export function MyFitnessPal({isDemo, setIsDemo, params, resolved}: Props) {
    const isAppScreens = useAppSelector((state) => state.scenes.isAppScreens);

    const checkmarkVariants = {
        hidden: {scale: 0, opacity: 0},
        visible: {
            scale: [0, 1.5, 1],
            opacity: 1,
        },
    };
    const delay = 0;

    return (
        <div className="flex h-full flex-col py-2.5">
            {/* Header */}
            <div className="mt-2 mb-4 text-center">
                <div className="flex flex-col items-center text-gray-700">
                    <graphics.DarkLogoWide className="size-62 h-auto" />
                </div>
                <motion.div
                    layout
                    className="
                        mt-1 inline-flex items-center justify-center text-center text-sm
                        font-normal text-gray-600
                    "
                >
                    <NumberFlow
                        value={isDemo ? 5 : 0}
                        format={{useGrouping: true}}
                        className="mx-1 mb-px text-base text-gray-800"
                    />
                    years,
                    <NumberFlow
                        value={isDemo ? 9 : 0}
                        format={{useGrouping: true}}
                        className="mx-1 mb-px text-base text-gray-800"
                    />
                    months{" "}
                    <motion.span className="mx-2 text-indigo-700" layout>
                        •
                    </motion.span>
                    <motion.span className="inline-flex items-center" layout>
                        <NumberFlow
                            value={isDemo ? 2094 : 0}
                            format={{useGrouping: true}}
                            className="mx-1 mb-px text-base text-gray-800"
                        />{" "}
                        <span>days tracked</span>
                    </motion.span>
                </motion.div>

                {isAppScreens == false && (
                    <div className="mt-4">
                        <div className="h-8 overflow-hidden rounded-lg bg-stone-200/90">
                            <motion.div
                                className="relative h-full rounded-lg bg-indigo-600"
                                initial={{width: "0%", opacity: 0}}
                                animate={
                                    isDemo
                                        ? {
                                              width: ["0%", "105%", "100%"],
                                              opacity: [0, 1, 1],
                                          }
                                        : {width: "0%", opacity: 0}
                                }
                                transition={{
                                    delay,
                                    width: {
                                        duration: 1.5,
                                        ease: [0.43, 0.13, 0.23, 0.96],
                                    },
                                    opacity: {
                                        duration: 0.3,
                                    },
                                }}
                            >
                                <motion.div
                                    className="absolute inset-0 bg-white/30"
                                    initial={{x: "-100%"}}
                                    animate={isDemo ? {x: "200%"} : {x: "-100%"}}
                                    transition={{
                                        delay: delay + 0.3,
                                        duration: 1.5,
                                        ease: "easeInOut",
                                    }}
                                    style={{
                                        background:
                                            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)",
                                    }}
                                />
                                <div
                                    className="
                                        absolute inset-0 flex items-center
                                        justify-center
                                    "
                                >
                                    <div className="text-lg font-medium text-white">
                                        100%
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                )}
            </div>

            {/* Migration Report */}

            {/* Migration Results */}
            <div className="mb-6 flex flex-1 flex-col justify-between">
                {/* Journal Entries Migrated */}
                <div className="rounded-2xl bg-blue-100 px-3 py-4">
                    <div className="flex items-center">
                        <div
                            className="
                                mr-3 flex size-10 items-center justify-center
                                rounded-full bg-blue-200
                            "
                        >
                            <DocumentTextIcon className="-mt-0.5 -mr-0.5 size-5 text-lg text-blue-600" />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-base/5 font-normal">Food diary</h4>
                            <p
                                className="
                                    inline-flex items-center gap-1 text-sm text-gray-700
                                "
                            >
                                <NumberFlow
                                    className="
                                        mb-px text-base font-normal text-blue-800
                                    "
                                    value={isDemo ? 12343 : 0}
                                    format={{useGrouping: true}}
                                />{" "}
                                entries imported
                            </p>
                        </div>
                        <motion.div
                            className="
                                flex size-8 items-center justify-center rounded-full
                                bg-blue-200/50
                            "
                            variants={checkmarkVariants}
                            initial="hidden"
                            animate={isDemo ? "visible" : "hidden"}
                            transition={{
                                delay: 0,
                                duration: 0.3,
                            }}
                        >
                            <SimpleIcon
                                icon="custom-bolder-check"
                                className="text-sm text-blue-600"
                            />
                        </motion.div>
                    </div>
                </div>

                {/* Measurements Migrated */}
                <div className="rounded-2xl bg-purple-100 px-3 py-4">
                    <div className="flex items-center">
                        <div
                            className="
                                mr-3 flex size-10 items-center justify-center
                                rounded-full bg-purple-200
                            "
                        >
                            <ScaleIcon className="size-5 text-lg text-purple-600" />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-base/5 font-normal">
                                Weight & Measurements
                            </h4>
                            <p
                                className="
                                    inline-flex items-center gap-1 text-sm text-gray-700
                                "
                            >
                                <NumberFlow
                                    className="
                                        mb-px text-base font-normal text-purple-800
                                    "
                                    value={isDemo ? 823 : 0}
                                    format={{useGrouping: true}}
                                />{" "}
                                measurements imported
                            </p>
                        </div>
                        <motion.div
                            className="
                                flex size-8 items-center justify-center rounded-full
                                bg-purple-200/50
                            "
                            variants={checkmarkVariants}
                            initial="hidden"
                            animate={isDemo ? "visible" : "hidden"}
                            transition={{
                                delay: 0.3,
                                duration: 0.3,
                            }}
                        >
                            <SimpleIcon
                                icon="custom-bolder-check"
                                className="text-sm text-purple-600"
                            />
                        </motion.div>
                    </div>
                </div>

                {/* Recipes Migrated */}
                <div className="rounded-2xl bg-sky-100 px-3 py-4">
                    <div className="flex items-center">
                        <div
                            className="
                                mr-3 flex size-10 items-center justify-center
                                rounded-full bg-sky-200
                            "
                        >
                            <SimpleIcon
                                icon="0532-chef"
                                className="text-lg text-sky-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-base/5 font-normal">
                                Personal Recipes
                            </h4>
                            <p
                                className="
                                    inline-flex items-center gap-1 text-sm text-gray-700
                                "
                            >
                                <NumberFlow
                                    className="mb-px text-base font-normal text-sky-800"
                                    value={isDemo ? 54 : 0}
                                />{" "}
                                recipes imported
                            </p>
                        </div>
                        <motion.div
                            className="
                                flex size-8 items-center justify-center rounded-full
                                bg-sky-200/50
                            "
                            variants={checkmarkVariants}
                            initial="hidden"
                            animate={isDemo ? "visible" : "hidden"}
                            transition={{
                                delay: 0.6,
                                duration: 0.3,
                            }}
                        >
                            <SimpleIcon
                                icon="custom-bolder-check"
                                className="text-sm text-sky-600"
                            />
                        </motion.div>
                    </div>
                </div>

                {/* Workouts Migrated */}
                <div className="rounded-2xl bg-rose-100 px-3 py-4">
                    <div className="flex items-center">
                        <div
                            className="
                                mr-3 flex size-10 items-center justify-center
                                rounded-full bg-rose-200
                            "
                        >
                            <SimpleIcon
                                icon="0611-bench-press"
                                className="text-lg text-rose-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-base/5 font-normal">
                                Workouts & Exercises
                            </h4>
                            <p
                                className="
                                    inline-flex items-center gap-1 text-sm text-gray-700
                                "
                            >
                                <NumberFlow
                                    className="
                                        mb-px text-base font-normal text-rose-800
                                    "
                                    value={isDemo ? 752 : 0}
                                    format={{useGrouping: true}}
                                />{" "}
                                sessions imported
                            </p>
                        </div>
                        <motion.div
                            className="
                                flex size-8 items-center justify-center rounded-full
                                bg-rose-200/50
                            "
                            variants={checkmarkVariants}
                            initial="hidden"
                            animate={isDemo ? "visible" : "hidden"}
                            transition={{
                                delay: 0.9,
                                duration: 0.3,
                            }}
                        >
                            <SimpleIcon
                                icon="custom-bolder-check"
                                className="text-sm text-rose-600"
                            />
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
                <button
                    onClick={() => {
                        if (isDemo == true) {
                            router.push("journal.journal", params, resolved);
                        } else {
                            setIsDemo(true);
                        }
                    }}
                    className={classNames(
                        "w-full bg-indigo-600 text-white font-medium py-4 rounded-xl hover:bg-indigo-700 transition-colors",
                        isDemo ? "active:bg-indigo-800" : "",
                    )}
                >
                    Start tracking
                </button>
                {/* Secondary Actions */}
                <div className="grid grid-cols-2 gap-3">
                    <button
                        onClick={() => {
                            setIsDemo(false);
                        }}
                        className={classNames(
                            "col-span-2 bg-stone-200 text-gray-700 py-3 px-4 rounded-xl hover:bg-stone-300 transition-colors text-sm font-medium",
                        )}
                    >
                        Learn more
                    </button>
                </div>
            </div>

            {/* Footer */}
            <p className="mt-2 text-center text-xs/relaxed text-gray-600">
                Nutrition <span className="text-indigo-700">•</span> Workouts{" "}
                <span className="text-indigo-700">•</span> Habits
            </p>
        </div>
    );
}

export const MyFitnessPalScreen = router.MyFitnessPal.withHooks(() => {
    const [isDemo, setIsDemo] = React.useState(false);
    return {
        isDemo,
        setIsDemo,
    };
})
    .withIndex((props) => <MyFitnessPal {...props} />)
    .withActions(() => [])
    .withOptions((props) => ({
        title: "",
        titleSection: (
            <>
                <graphics.Mfp
                    style={{color: "#0066EE"}}
                    className="mx-auto mt-1.5 h-7 w-auto"
                    title="MyFitnessPal"
                />
                <div
                    className="
                        absolute inset-x-0 z-100 mt-2 flex items-center justify-center
                        text-gray-700
                    "
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="size-7"
                    >
                        <path
                            fillRule="evenodd"
                            d="M2.24 6.8a.75.75 0 0 0 1.06-.04l1.95-2.1v8.59a.75.75 0 0 0 1.5 0V4.66l1.95 2.1a.75.75 0 1 0 1.1-1.02l-3.25-3.5a.75.75 0 0 0-1.1 0L2.2 5.74a.75.75 0 0 0 .04 1.06Zm8 6.4a.75.75 0 0 0-.04 1.06l3.25 3.5a.75.75 0 0 0 1.1 0l3.25-3.5a.75.75 0 1 0-1.1-1.02l-1.95 2.1V6.75a.75.75 0 0 0-1.5 0v8.59l-1.95-2.1a.75.75 0 0 0-1.06-.04Z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
            </>
        ),
        scene: {kind: "FULL"},
        more: <Streak value={props.isDemo ? 147 : 0} />,
    }));
